/* globals $ */

import PropTypes from "prop-types";
import React from "react";
import { GoogleReCaptcha } from "react-google-recaptcha-v3";
import queryString from "query-string";

import ClientStats from "shared/util/ClientStats";
import isApp from "shared/util/isApp";
import { isIOS } from "shared/util/EnvironmentHelpers";
import _str from "shared/util/_str";
import { locationPropTypes, historyPropTypes } from "shared/constants/propTypes";
import AppPhoneCommand from "shared/util/AppPhoneCommand";
import { CONFIRM_RECAPTCHA_SUCCESS } from "ducks/login";
import { setDeviceId } from "shared/util/deviceId";

import Login from "apps/login/components/screens/Login";
import PasswordReset from "apps/login/components/screens/PasswordReset";
import PasswordSubmit from "apps/login/components/screens/PasswordSubmit";
import SmsPhone from "apps/login/components/screens/SmsPhone";
import SmsCode from "apps/login/components/screens/SmsCode";
import Sms2Factor from "apps/login/components/screens/Sms2Factor";
import SmsStatus from "apps/login/components/screens/SmsStatus";
import Reactivate from "apps/login/components/screens/Reactivate";
import LoginCaptcha from "apps/login/components/screens/LoginCaptcha";
import WebviewCaptchaLogin from "apps/login/components/screens/WebviewCaptchaLogin";
import FeedbackBanner from "shared/util/FeedbackBanner";
import withBackgroundWrapper from "apps/login/components/withBackgroundWrapper";

import { SCREENS, DIRECT_LINKABLE_SCREENS, CUSTOM_ERRORS } from "apps/login/util/constants";

import "./App.scss";

if (process.env.PLATFORM === "desktop") {
	require("./App.desktop.scss");
} else {
	require("./App.mobile.scss");
}

const isFacebookError = ({ type }) => type === CUSTOM_ERRORS.FACEBOOK_EXITED;


const maybeSetDeviceIdFromQuery = (props) => {
	const { deviceId } = queryString.parse(props.location.search);

	return deviceId && setDeviceId(deviceId);
};

class App extends React.Component {
	static propTypes = {
		screen: PropTypes.string,
		loadLogin: PropTypes.func.isRequired,
		submitLogin: PropTypes.func.isRequired,
		reactivateAccount: PropTypes.func.isRequired,
		facebookLogin: PropTypes.func.isRequired,
		submitReset: PropTypes.func.isRequired,
		changeScreen: PropTypes.func.isRequired,
		showFacebookLogin: PropTypes.bool,
		showTwoFactor: PropTypes.bool.isRequired,
		isSubmitting: PropTypes.bool.isRequired,
		isLoading: PropTypes.bool.isRequired,
		isLoggedIn: PropTypes.bool.isRequired,
		error: PropTypes.shape({
			type: PropTypes.node,
			message: PropTypes.node,
		}),
		confirmRecaptchaToken: PropTypes.func.isRequired,
		location: locationPropTypes.isRequired,
		history: historyPropTypes.isRequired,
		smsLogin: PropTypes.shape({
			isSubmitting: PropTypes.bool,
			error: PropTypes.string,
			hasCodeValidationError: PropTypes.bool,
			hasCodeSendError: PropTypes.bool,
			isSendingCode: PropTypes.bool,
			isSmsTwoFactor: PropTypes.bool,
		}).isRequired,
		sms: PropTypes.shape({
			phoneNumber: PropTypes.string.isRequired,
			dialingCode: PropTypes.string.isRequired,
		}).isRequired,
		sendOTPCode: PropTypes.func.isRequired,
		submitOTPCodeAndLogin: PropTypes.func.isRequired,
		resendOTPCode: PropTypes.func.isRequired,
		resendOTPCodeSuccess: PropTypes.func.isRequired,
		resendOTPCodeFailed: PropTypes.func.isRequired,
		send2FaOTP: PropTypes.func.isRequired,
		validate2FaOTP: PropTypes.func.isRequired,
		saveReactivateParams: PropTypes.func.isRequired,
	};

	static defaultProps = {
		showFacebookLogin: false,
		error: null,
		screen: null,
	};

	state = {
		username: "",
		password: "",
		captchaKey: null,
		authcode: "",
		saveAuth: false,
	};

	componentDidMount() {
		const { loadLogin, screen, location, changeScreen, saveReactivateParams } = this.props;
		const { step, email, reenableUserid, reenableAuthCode } = queryString.parse(location.search);

		ClientStats.update({
			statname: `login - onboarding funnel - view:${screen}`,
			unique: true,
		});

		loadLogin();

		if (isApp()) {
			$(this.container).immobilize(true, true);
		}

		// clear the query string so direct link screens do not stay rendered
		this.clearQueryString();

		if (step === DIRECT_LINKABLE_SCREENS.PASSWORD_RESET) {
			changeScreen(SCREENS.PASSWORD_RESET);
		} else if (step === DIRECT_LINKABLE_SCREENS.CAPTCHA) {
			changeScreen(SCREENS.CAPTCHA);
		} else if (step === DIRECT_LINKABLE_SCREENS.REACTIVATE) {
			changeScreen(SCREENS.REACTIVATE);
		} else if (!screen) {
			// if there are no direct linked screens then render login
			changeScreen(SCREENS.LOGIN);
		}

		if (email) {
			this._changeField("username", decodeURIComponent(email));
		}

		if (reenableUserid && reenableAuthCode) {
			saveReactivateParams(
				decodeURIComponent(reenableUserid),
				decodeURIComponent(reenableAuthCode));
		}

		// set the deviceid if passed from the native clients used for login call
		// for the backend to check if the user needs 2fa
		maybeSetDeviceIdFromQuery(this.props);

		// fire this so iOS knows that the page has loaded
		// so they can make the transition to webview nice
		AppPhoneCommand("smsLoginReady");
	}

	componentWillReceiveProps(nextProps) {
		if (
			nextProps.error
			&& nextProps.error !== this.props.error
			&& !isFacebookError(nextProps.error)
		) {
			FeedbackBanner.open({
				message: nextProps.error.message,
				type: "error",
				timeout: 3000,
			});
		}

		if (nextProps.screen !== this.props.screen) {
			ClientStats.update({
				statname: `login - onboarding funnel - view:${nextProps.screen}`,
				unique: true,
			});
		}
	}

	clearQueryString = () => {
		const { location, history } = this.props;

		// remove the search from the url to prevent and direct link screens from staying rendered
		// since they are always rendered if the query: step=captcha or step=reset_password
		const nextSearch = queryString.stringify({
			...queryString.parse(location.search),
			step: undefined,
			email: undefined,
		});

		history.replace({
			pathname: location.pathName,
			search: nextSearch,
		});
	}

	_changeField = (field, value) => {
		this.setState({ [field]: value });
	};

	_onChangeUsername = (ev) => {
		this.setState({ username: ev.target.value });
	};

	_onChangePassword = (ev) => {
		this.setState({ password: ev.target.value });
	};

	_submitReset = (email) => {
		this.props.submitReset(email);
	};

	_submitLogin = () => {
		// Uses what's in state, so no args here.
		this.props.submitLogin({
			username: this.state.username,
			password: this.state.password,
			captchaKey: this.state.captchaKey,
			authcode: this.state.authcode,
			saveAuth: this.state.saveAuth,
		});
	};

	_reactivateAccount = () => {
		const notifyIOS = isApp() && isIOS;

		this.props.reactivateAccount({ notifyIOS });
	};

	_facebookLogin = () => {
		this.props.facebookLogin();
	};

	_onCaptchaVerify = (captchaKey) => {
		this.setState({ captchaKey });
	}

	_onVerifyCaptchaTrackEvent = (captchaKey) => {
		const {
			confirmRecaptchaToken,
			screen,
		} = this.props;

		confirmRecaptchaToken({
			type: CONFIRM_RECAPTCHA_SUCCESS,
			token: captchaKey,
			isCaptchaShowing: screen === SCREENS.CAPTCHA,
			page: "login",
		});
	}

	_onSubmit = (ev) => {
		ev.preventDefault();
		this.props.submitLogin(this.state.username, this.state.password);
	};

	handleLoginWithPhone = () => {
		if (isApp() && isIOS) {
			AppPhoneCommand("smsLogInWithPhone");
			return;
		}

		this.props.changeScreen(SCREENS.SMS_PHONE_INPUT);
	}

	render() {
		const {
			screen,
			changeScreen,
			showFacebookLogin,
			showTwoFactor,
			isSubmitting,
			isLoading,
			isLoggedIn,
			sendOTPCode,
			submitOTPCodeAndLogin,
			send2FaOTP,
			validate2FaOTP,
			sms,
			smsLogin,
			resendOTPCode,
			resendOTPCodeSuccess,
			resendOTPCodeFailed,
		} = this.props;
		const {
			username,
			password,
			authcode,
			saveAuth,
			captchaKey,
		} = this.state;

		let screenEl;
		let screenTitle;

		if (screen === SCREENS.PASSWORD_RESET) {
			screenTitle = _str("Enter your email to reset your password");
			screenEl = (
				<PasswordReset
					submitReset={this._submitReset}
					changeScreen={changeScreen}
					username={username}
					isLoading={isLoading}
				/>
			);
		} else if (screen === SCREENS.CAPTCHA) {
			screenTitle = _str("Enter email and password");
			screenEl = (
				<WebviewCaptchaLogin
					captchaKey={captchaKey}
					onCaptchaVerify={this._onCaptchaVerify}
					isLoggingIn={isSubmitting}
					isLoggedIn={isLoggedIn}
					submitLogin={this._submitLogin}
					changeField={this._changeField}
					username={username}
					password={password}
					isSubmitting={isSubmitting}
				/>
			);
		} else if (screen === SCREENS.CAPTCHA) {
			screenTitle = _str("Enter email and password");
			screenEl = (
				<LoginCaptcha
					onSignIn={this._submitLogin}
					onCaptchaVerify={this._onCaptchaVerify}
					isLoggingIn={isSubmitting}
					isLoggedIn={isLoggedIn}
				/>
			);
		} else if (screen === SCREENS.LOGIN) {
			screenTitle = _str("Enter email and password");
			screenEl = (
				<Login
					submitLogin={this._submitLogin}
					showFacebookLogin={!!showFacebookLogin}
					facebookLogin={this._facebookLogin}
					phoneLogin={this.handleLoginWithPhone}
					changeField={this._changeField}
					changeScreen={changeScreen}
					username={username}
					password={password}
					isSubmitting={isSubmitting}
					isLoggedIn={isLoggedIn}
					showTwoFactor={showTwoFactor}
					authcode={authcode}
					saveAuth={saveAuth}
				/>
			);
		} else if (screen === SCREENS.PASSWORD_SUBMIT) {
			screenTitle = _str("We’ve sent you an email to reset your password");

			screenEl = (
				<PasswordSubmit changeScreen={changeScreen} />
			);
		} else if (screen === SCREENS.REACTIVATE) {
			if (process.env.PLATFORM === "desktop") {
				screenTitle = _str("Howdy");
			}

			screenEl = (
				<Reactivate submitLogin={this._reactivateAccount} />
			);
		} else if (screen === SCREENS.SMS_PHONE_INPUT) {
			screenTitle = _str("What’s your phone number?");
			screenEl = (
				<SmsPhone
					isSubmitting={smsLogin.isSubmitting}
					sendOTPCode={sendOTPCode}
					dialingCode={sms.dialingCode}
					phoneNumber={sms.phoneNumber}
				/>
			);
		} else if (screen === SCREENS.SMS_CODE_INPUT) {
			screenTitle = _str("Enter the code we sent to your phone number");
			screenEl = (
				<SmsCode
					changeScreen={changeScreen}
					isSubmitting={smsLogin.isSubmitting}
					submitOTPCode={smsLogin.isSmsTwoFactor ? validate2FaOTP : submitOTPCodeAndLogin}
					resendOTPCode={resendOTPCode}
					resendOTPCodeSuccess={resendOTPCodeSuccess}
					resendOTPCodeFailed={resendOTPCodeFailed}
					validationError={smsLogin.hasCodeValidationError}
					fullPhoneNumber={`${sms.dialingCode}${sms.phoneNumber}`}
				/>
			);
		} else if (screen === SCREENS.SMS_SENDING_CODE) {
			screenEl = (
				<SmsStatus loading message={_str("Sending Code")} />
			);
		} else if (screen === SCREENS.SMS_VALIDATING_CODE) {
			screenEl = (
				<SmsStatus loading message={_str("Validating Code")} />
			);
		} else if (screen === SCREENS.SMS_SUCCESS) {
			screenEl = (
				<SmsStatus message={_str("Success!")} />
			);
		} else if (screen === SCREENS.SMS_TWO_FACTOR) {
			screenTitle = _str("Logging in on a new device");
			screenEl = (
				<Sms2Factor
					isSendingCode={smsLogin.isSendingCode}
					send2FaOTP={send2FaOTP}
				/>
			);
		}

		return (
			<div className="login" ref={(el) => { this.container = el; }}>
				{screenTitle && (
					<div className="login-header">
						<span key={screenTitle} className={`login-header-title login-header-title-${screen}`}>
							{screenTitle}
						</span>
					</div>
				)}
				<div className="login-container">
					<div className="loginscreen" key={screen}>
						{screenEl}
					</div>
				</div>
				<GoogleReCaptcha
					onVerify={this._onVerifyCaptchaTrackEvent}
					action="login"
				/>
			</div>
		);
	}
}

export default withBackgroundWrapper(App);



// WEBPACK FOOTER //
// ./src/apps/login/components/App.jsx